<script setup lang="ts">
  // Adapted from cartadigital-pos app/components/pos/PosItemImage.vue using
  // admin's item image URL convention (Globals.uploadsPath, see
  // StoreMenuItemPicture.vue). Plain <img> — Vuetify-free, lazy by default.
  import { computed, ref, watch } from 'vue';
  import { IItem } from '@/interfaces/IItem';
  import { Globals } from '@/modules/Globals';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const props = withDefaults(
    defineProps<{
      item?: IItem | null;
      loading?: 'lazy' | 'eager';
    }>(),
    {
      item: null,
      loading: 'lazy',
    }
  );

  const errorLoading = ref(false);

  // Square-POS-style placeholder: items without photo get a monogram on a
  // deterministic muted tint, so the grid stays scannable. Palette skips
  // brand orange and the status colors (emerald/amber/red) on purpose.
  const PLACEHOLDER_TINTS = [
    'rose',
    'sky',
    'violet',
    'teal',
    'indigo',
    'fuchsia',
    'cyan',
    'lime',
  ] as const;

  const initials = computed(() => {
    const name = props.item?.name?.trim();
    if (!name) return null;
    const words = name
      .split(/\s+/)
      .filter((word) => /^[\p{L}\p{N}]/u.test(word));
    if (words.length === 0) return null;
    return (
      words
        .slice(0, 2)
        // Array.from keeps surrogate pairs (non-BMP letters) whole.
        .map((word) => Array.from(word)[0].toUpperCase())
        .join('')
    );
  });

  const tintClass = computed(() => {
    const name = props.item?.name ?? '';
    let hash = 0;
    for (const char of name) {
      hash = (hash * 31 + (char.codePointAt(0) ?? 0)) >>> 0;
    }
    return `tint-${PLACEHOLDER_TINTS[hash % PLACEHOLDER_TINTS.length]}`;
  });

  const src = computed(() => {
    if (!props.item?.picture) return null;
    return `${Globals.uploadsPath}/${props.item.picture}`;
  });

  watch(src, () => {
    errorLoading.value = false;
  });
</script>

<template>
  <img
    v-if="src && !errorLoading"
    class="pos-v2-item-image"
    :src="src"
    :alt="item ? `Foto de ${item.name}` : 'Foto del producto'"
    :loading="loading"
    @error="errorLoading = true"
  />
  <div
    v-else
    class="pos-v2-item-image-placeholder"
    :class="initials ? tintClass : null"
    aria-hidden="true"
  >
    <span v-if="initials" class="placeholder-initials">{{ initials }}</span>
    <PosUiIcon v-else name="image-off" class="placeholder-icon" />
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-item-image {
    @apply tw-h-full tw-w-full tw-object-cover;
  }

  .pos-v2-item-image-placeholder {
    @apply tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-neutral-200 tw-text-neutral-500;

    .placeholder-initials {
      @apply tw-select-none tw-text-xl tw-font-semibold tw-leading-none;
    }

    .placeholder-icon {
      @apply tw-text-2xl;
    }

    &.tint-rose {
      @apply tw-bg-rose-100 tw-text-rose-600;
    }

    &.tint-sky {
      @apply tw-bg-sky-100 tw-text-sky-600;
    }

    &.tint-violet {
      @apply tw-bg-violet-100 tw-text-violet-600;
    }

    &.tint-teal {
      @apply tw-bg-teal-100 tw-text-teal-600;
    }

    &.tint-indigo {
      @apply tw-bg-indigo-100 tw-text-indigo-600;
    }

    &.tint-fuchsia {
      @apply tw-bg-fuchsia-100 tw-text-fuchsia-600;
    }

    &.tint-cyan {
      @apply tw-bg-cyan-100 tw-text-cyan-600;
    }

    &.tint-lime {
      @apply tw-bg-lime-100 tw-text-lime-700;
    }
  }
</style>
